import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { CalculationHistoryEntry } from '../engine/types';

interface HistoryContextType {
  entries: CalculationHistoryEntry[];
  addEntry: (entry: Omit<CalculationHistoryEntry, 'id' | 'timestamp'>) => void;
  removeEntry: (id: string) => void;
  renameEntry: (id: string, name: string) => void;
  clearHistory: () => void;
  getEntry: (id: string) => CalculationHistoryEntry | undefined;
}

const HistoryContext = createContext<HistoryContextType | null>(null);

const STORAGE_KEY = 'bgd-calc-history';
const MAX_ENTRIES = 100;

export function HistoryProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<CalculationHistoryEntry[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage quota exceeded - trim oldest entries
      const trimmed = entries.slice(-MAX_ENTRIES / 2);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    }
  }, [entries]);

  const addEntry = useCallback((entry: Omit<CalculationHistoryEntry, 'id' | 'timestamp'>) => {
    const newEntry: CalculationHistoryEntry = {
      ...entry,
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
    };

    setEntries(prev => {
      const updated = [newEntry, ...prev];
      return updated.slice(0, MAX_ENTRIES);
    });
  }, []);

  const removeEntry = useCallback((id: string) => {
    setEntries(prev => prev.filter(e => e.id !== id));
  }, []);

  const renameEntry = useCallback((id: string, name: string) => {
    setEntries(prev => prev.map(e =>
      e.id === id ? { ...e, name: name.trim() || undefined } : e
    ));
  }, []);

  const clearHistory = useCallback(() => {
    setEntries([]);
  }, []);

  const getEntry = useCallback((id: string) => {
    return entries.find(e => e.id === id);
  }, [entries]);

  return (
    <HistoryContext.Provider value={{ entries, addEntry, removeEntry, renameEntry, clearHistory, getEntry }}>
      {children}
    </HistoryContext.Provider>
  );
}

// Hook co-located with its provider; moving it would churn 10 importers for a fast-refresh-only concern.
// eslint-disable-next-line react-refresh/only-export-components
export function useHistory() {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useHistory must be used within a HistoryProvider');
  }
  return context;
}

export { HistoryContext };
